import { Link } from "@tanstack/react-router";
import { useLayoutEffect, useState, type CSSProperties, type KeyboardEvent } from "react";

export type HomeServiceCard = {
  n: string;
  slug: string;
  title: string;
  body: string;
  link: string;
};

type ServiceImage = {
  src: string;
  srcSet?: string;
  width: number;
  height: number;
  alt: string;
  position?: string;
};

function cardSrcSet(name: string) {
  return [640, 1024, 1280].map((w) => `/images/optimised/${name}-${w}.webp ${w}w`).join(", ");
}

const serviceImages: Record<string, ServiceImage> = {
  "textile-recycling": {
    src: "/images/optimised/textile-recycling-card-1280.webp",
    srcSet: cardSrcSet("textile-recycling-card"),
    width: 1280,
    height: 720,
    alt: "Bales of sorted clothing stacked in a warehouse beside a forklift",
  },
  "confidential-waste-disposal": {
    src: "/images/optimised/confidential-waste-card-1280.webp",
    srcSet: cardSrcSet("confidential-waste-card"),
    width: 1280,
    height: 720,
    alt: "Lockable console with a paper slot in a bright open plan office",
  },
  "it-and-media-recycling": {
    src: "/images/optimised/it-media-card-1280.webp",
    srcSet: cardSrcSet("it-media-card"),
    width: 1280,
    height: 720,
    alt: "Retired laptops in a crate beside hard drives and USB sticks",
  },
  "product-destruction": {
    src: "/images/optimised/locked-mesh-cage-960.webp",
    width: 960,
    height: 1200,
    alt: "Padlocked mesh cage holding sealed sacks",
    position: "center",
  },
  "one-off-shredding": {
    src: "/images/optimised/one-off-shredding-1280.webp",
    srcSet: cardSrcSet("one-off-shredding"),
    width: 1280,
    height: 720,
    alt: "Archive boxes and sacks of shredded paper ready for collection",
  },
};

type Props = {
  services: HomeServiceCard[];
};

/**
 * Homepage service row. One card is open. On a wide screen the open card
 * widens and the others settle into title strips. Narrow screens keep a
 * stacked layout with every service readable. Content is in the server HTML.
 */
export function ExpandingServiceCards({ services }: Props) {
  const [active, setActive] = useState(0);
  const [enhanced, setEnhanced] = useState(false);
  const [wide, setWide] = useState(false);

  useLayoutEffect(() => {
    const wideQuery = window.matchMedia("(min-width: 1024px)");
    const sync = () => setWide(wideQuery.matches);
    sync();
    setEnhanced(true);
    wideQuery.addEventListener("change", sync);
    return () => wideQuery.removeEventListener("change", sync);
  }, []);

  function openCard(index: number) {
    setActive(index);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!wide) return;
    const key = event.key;
    const order = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
    if (!order.includes(key)) return;
    event.preventDefault();
    const last = services.length - 1;
    const next =
      key === "ArrowRight" || key === "ArrowDown"
        ? (index + 1) % services.length
        : key === "ArrowLeft" || key === "ArrowUp"
          ? (index - 1 + services.length) % services.length
          : key === "Home"
            ? 0
            : last;
    document.getElementById(`service-card-${services[next].slug}`)?.focus();
  }

  return (
    <div
      className="pe-service-row"
      data-enhanced={enhanced ? "true" : undefined}
      data-count={services.length}
      style={{ "--pe-count": services.length } as CSSProperties}
    >
      {services.map((service, index) => {
        const open = index === active;
        const image = serviceImages[service.slug];
        const buttonId = `service-card-${service.slug}`;
        const panelId = `service-panel-${service.slug}`;
        return (
          <article
            key={service.slug}
            className="pe-service-card"
            data-open={open ? "true" : "false"}
            onMouseEnter={() => {
              if (wide) openCard(index);
            }}
          >
            {image ? (
              <img
                className="pe-card-photo"
                src={image.src}
                srcSet={image.srcSet}
                width={image.width}
                height={image.height}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                sizes="(min-width: 1024px) 42rem, 100vw"
                style={image.position ? { objectPosition: image.position } : undefined}
              />
            ) : null}
            <div className="pe-card-shade pe-card-shade-rest" aria-hidden="true" />
            <div className="pe-card-shade pe-card-shade-open" aria-hidden="true" />
            <span className="pe-card-index" aria-hidden="true">
              {service.n}
            </span>
            <span className="pe-card-vert" aria-hidden="true">
              {service.title}
            </span>
            <div className="pe-card-stack">
              <p className="pe-card-kicker">
                {service.n} / {services.length.toString().padStart(2, "0")}
              </p>
              <h3>
                <button
                  type="button"
                  id={buttonId}
                  className="pe-card-hit"
                  tabIndex={wide ? 0 : -1}
                  aria-expanded={wide ? open : undefined}
                  aria-controls={wide ? panelId : undefined}
                  onClick={() => openCard(index)}
                  onFocus={() => {
                    if (wide) openCard(index);
                  }}
                  onKeyDown={(event) => onKeyDown(event, index)}
                >
                  <span className="pe-card-horiz">{service.title}</span>
                </button>
              </h3>
              <div
                id={panelId}
                className="pe-card-panel"
                role={wide && open ? "region" : undefined}
                aria-labelledby={wide && open ? buttonId : undefined}
              >
                <p className="pe-card-body">{service.body}</p>
                <Link to="/services/$slug" params={{ slug: service.slug }} className="pe-card-link">
                  {service.link} →
                </Link>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
